import { AuthenticationResult } from "@azure/msal-browser";
import React, { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { APITokenContext } from "@eaigle/ui";
import { useServices } from "../contexts/ServicesContext";
import { setAuthToken } from "@eaigle/api/AxiosWrappers";
import {
  IAuthenticationService,
  authService as realAuthService,
} from "@eaigle/api/authService";

const REFRESH_BUFFER_MS = 5 * 60 * 1000;

type Props = {
  authService?: IAuthenticationService;
  children: ReactNode;
};

export const APITokenProvider: React.FC<Props> = ({
  children,
  authService = realAuthService,
}) => {
  const navigate = useNavigate();
  const [authResult, setAuthResult] = useState<AuthenticationResult | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const refreshTimeoutId = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { axiosConfigurer } = useServices();

  const clearRefreshTimeout = useCallback(() => {
    if (refreshTimeoutId.current) {
      clearTimeout(refreshTimeoutId.current);
      refreshTimeoutId.current = null;
    }
  }, []);

  const scheduleRefresh = useCallback(
    (result: AuthenticationResult | null) => {
      clearRefreshTimeout();

      if (result && result.expiresOn) {
        const now = Date.now();
        const expiryTime = result.expiresOn.getTime();
        let refreshDelay = expiryTime - now - REFRESH_BUFFER_MS;

        if (refreshDelay <= 0) {
          // console.warn('Token is expired or expires very soon. Refreshing immediately.');
          refreshDelay = 0;
        }

        // console.log(`Scheduling token refresh in ${Math.round(refreshDelay / 1000)} seconds.`);

        // TODO: Implement refresh token error handling
        refreshTimeoutId.current = setTimeout(async () => {
          // console.log('Attempting proactive token refresh...');
          await refreshToken(true);
        }, refreshDelay);
      }
    },

    [clearRefreshTimeout], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const refreshToken = useCallback(
    async (isProactiveRefresh = false) => {
      if (!isProactiveRefresh) {
        setIsLoading(true);
      }
      setError(null);

      try {
        const newAuthResult = await authService.getAccessToken();
        setAuthResult(newAuthResult);
        if (newAuthResult) {
          scheduleRefresh(newAuthResult);
          setError(null);
        } else {
          setError("Failed to acquire token. Interaction might be required.");
          clearRefreshTimeout();
        }
      } catch (err) {
        // console.error("Error during token refresh:", err);
        setError(err instanceof Error ? err.message : "Failed to get token");
        setAuthResult(null);
        clearRefreshTimeout();
      } finally {
        if (!isProactiveRefresh) {
          setIsLoading(false);
        }
      }
    },
    [authService, scheduleRefresh, clearRefreshTimeout],
  );

  useEffect(() => {
    void refreshToken();
    return clearRefreshTimeout;
  }, [refreshToken, clearRefreshTimeout]);

  // Proactively refresh token when the tab becomes visible again
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState !== "visible") {
        return;
      }

      // If we don't have a valid auth result yet, request a fresh token
      if (!authResult || !authResult.expiresOn) {
        void refreshToken(true);
        return;
      }

      const now = Date.now();
      const expiryTime = authResult.expiresOn.getTime();

      // If the token is expired or close to expiry, refresh it proactively
      if (expiryTime - now <= REFRESH_BUFFER_MS) {
        void refreshToken(true);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [authResult, refreshToken]);

  // Handle authentication events
  useEffect(() => {
    function handleLogoutError(error: Error) {
      console.error("Error during logout:", error);
      //void error; // Explicitly acknowledge the unused variable
      // Handle potential errors during logout redirect
      // As a fallback, attempt to navigate directly to the root path
      void navigate("/");
    }
    authService.on("logoutError", handleLogoutError);
    return () => {
      authService.off("logoutError", handleLogoutError);
    };
  }, [authService, navigate]);

  const token = authResult?.accessToken ?? null;

  // Wrap the token in a ref so downstream dependents can
  // remain constant through rerenders
  const apiTokenRef = useRef(token);

  // Update the wrapped value when the token changes
  useEffect(() => {
    apiTokenRef.current = token;
    setAuthToken(token);
    axiosConfigurer.setAuthToken(token);
  }, [token, apiTokenRef, axiosConfigurer]);

  return (
    <APITokenContext.Provider
      value={{ token, isLoading, error, refreshToken, apiTokenRef }}
    >
      {children}
    </APITokenContext.Provider>
  );
};
